import {
	Badge,
	Button,
	CloseButton,
	Group,
	Stack,
	Text,
	TextInput,
} from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { fillStyleText } from "@/locales/assistant-style";
import type { AssistantWaitPhrasesText } from "@/locales/assistant-wait-phrases";
import { DEFAULT_WAIT_PHRASES, STYLE_LIMITS } from "@/types/ai-assistant-style";

interface Props {
	t: AssistantWaitPhrasesText;
	phrases: string[];
	error?: string;
	disabled: boolean;
	onChange: (phrases: string[]) => void;
}

const same = (a: string, b: string) =>
	a.toLocaleLowerCase("id-ID") === b.toLocaleLowerCase("id-ID");

/** Chip kalimat tunggu GPT-Live (tambah/hapus) — maks 5 kalimat × 40 karakter. */
export function StyleWaitPhrases({
	t,
	phrases,
	error,
	disabled,
	onChange,
}: Props) {
	const [draft, setDraft] = useState("");
	const candidate = draft.trim().replace(/\s+/g, " ");
	const full = phrases.length >= STYLE_LIMITS.waitPhrasesMax;
	const canAdd =
		!disabled &&
		!full &&
		candidate !== "" &&
		!phrases.some((p) => same(p, candidate));

	const add = () => {
		if (!canAdd) return;
		onChange([...phrases, candidate]);
		setDraft("");
	};

	return (
		<Stack gap={6} data-wait-phrases>
			<Text fw={600}>{t.title}</Text>
			<Text size="sm" c="dimmed">
				{t.description}
			</Text>
			<Group gap={6}>
				{phrases.map((phrase) => (
					<Badge
						key={phrase}
						variant="light"
						size="lg"
						tt="none"
						data-wait-phrase={phrase}
						rightSection={
							<CloseButton
								size="xs"
								aria-label={fillStyleText(t.remove, { word: phrase })}
								data-wait-phrase-remove={phrase}
								disabled={disabled}
								onClick={() => onChange(phrases.filter((p) => p !== phrase))}
							/>
						}
					>
						{phrase}
					</Badge>
				))}
			</Group>
			<Group gap="xs" align="flex-start">
				<TextInput
					style={{ flex: 1, maxWidth: 320 }}
					placeholder={t.placeholder}
					maxLength={STYLE_LIMITS.waitPhraseCharsMax}
					value={draft}
					onChange={(e) => setDraft(e.currentTarget.value)}
					onKeyDown={(e) => {
						if (e.key !== "Enter") return;
						e.preventDefault();
						add();
					}}
					error={error}
					disabled={disabled || full}
					data-wait-phrase-input
				/>
				<Button
					variant="light"
					leftSection={<IconPlus size={14} />}
					onClick={add}
					disabled={!canAdd}
					data-wait-phrase-add
				>
					{t.add}
				</Button>
				<Button
					variant="subtle"
					onClick={() => onChange([...DEFAULT_WAIT_PHRASES])}
					disabled={disabled}
					data-wait-phrase-restore
				>
					{t.restoreDefault}
				</Button>
				<Text size="xs" c="dimmed" pt={8}>
					{phrases.length}/{STYLE_LIMITS.waitPhrasesMax}
				</Text>
			</Group>
		</Stack>
	);
}
